import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { useAuth } from "../AuthContext";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  ClipboardList, Plus, Search, CheckCircle2, PlayCircle, Paperclip,
  AlertTriangle, MessageCircle, Trash2, Flag, X,
} from "lucide-react";
import { toast } from "sonner";

const POLL_MS = 10000;

const PRIORITY_COLORS = {
  Low:     "text-slate-300 border-slate-600/60 bg-slate-600/10",
  Normal:  "text-blue-300 border-blue-500/40 bg-blue-500/10",
  High:    "text-amber-300 border-amber-500/40 bg-amber-500/10",
  Urgent:  "text-red-300 border-red-500/40 bg-red-500/10",
};
const STATUS_COLORS = {
  Open:          "text-slate-300 border-slate-600/60 bg-slate-600/10",
  "In progress": "text-sky-300 border-sky-500/40 bg-sky-500/10",
  Completed:     "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  Cancelled:     "text-slate-500 border-slate-700/60 bg-slate-700/10",
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result || "");
      const i = s.indexOf(",");
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function Tasks() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({ by_assignee: [] });
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState({
    status: "", priority: "", assignee_id: "",
    overdue: false, mine: false, q: "",
  });
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/tasks", { params: {
          status: filter.status || undefined,
          priority: filter.priority || undefined,
          assignee_id: filter.assignee_id || undefined,
          overdue: filter.overdue || undefined,
          mine: filter.mine || undefined,
          q: filter.q || undefined,
        }}),
        api.get("/tasks/summary"),
      ]);
      setTasks(t.data);
      setSummary(s.data);
    } catch { /* silent */ }
  }, [filter]);

  const loadStaff = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/staff");
      setStaff(data.filter((u) => u.is_active !== false));
    } catch { /* silent */ }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStaff(); }, [loadStaff]);
  useEffect(() => {
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  const setStatus = async (t, status) => {
    try {
      await api.post(`/tasks/${t.id}/status`, { status });
      toast.success(status === "Completed" ? "Marked complete" : `Status → ${status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeTask = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await api.delete(`/tasks/${t.id}`);
      toast.success("Task deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout title="Tasks">
      <div className="space-y-4" data-testid="tasks-page">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-xl font-semibold tracking-tight flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-sky-400" /> Tasks
          </h1>
          <div className="flex-1" />
          {isAdmin && (
            <Button onClick={() => setAddOpen(true)}
              className="bg-sky-600 hover:bg-sky-500 text-white" data-testid="add-task-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New task
            </Button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ScoreCard label="Open" value={summary.open || 0} />
          <ScoreCard label="In progress" value={summary.in_progress || 0} tone="sky" />
          <ScoreCard label="Overdue" value={summary.overdue || 0} tone="red" />
          <ScoreCard label="Completed" value={summary.completed || 0} tone="emerald" />
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input placeholder="Search title / description…"
              value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })}
              className="bg-[#0a0e1a] border-slate-800 pl-8 h-9 w-56" data-testid="tasks-search" />
          </div>
          <Select value={filter.status || "all"}
            onValueChange={(v) => setFilter({ ...filter, status: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9 w-40" data-testid="tasks-filter-status">
              <SelectValue placeholder="Any status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any status</SelectItem>
              <SelectItem value="Open">Open</SelectItem>
              <SelectItem value="In progress">In progress</SelectItem>
              <SelectItem value="Completed">Completed</SelectItem>
              <SelectItem value="Cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter.priority || "all"}
            onValueChange={(v) => setFilter({ ...filter, priority: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9 w-36">
              <SelectValue placeholder="Any priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any priority</SelectItem>
              <SelectItem value="Low">Low</SelectItem>
              <SelectItem value="Normal">Normal</SelectItem>
              <SelectItem value="High">High</SelectItem>
              <SelectItem value="Urgent">Urgent</SelectItem>
            </SelectContent>
          </Select>
          {isAdmin && (
            <Select value={filter.assignee_id || "all"}
              onValueChange={(v) => setFilter({ ...filter, assignee_id: v === "all" ? "" : v })}>
              <SelectTrigger className="bg-[#0a0e1a] border-slate-800 h-9 w-52" data-testid="tasks-filter-assignee">
                <SelectValue placeholder="All assignees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All assignees</SelectItem>
                {staff.map((s) => <SelectItem key={s.id} value={s.id}>{s.display_name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
            <Checkbox checked={filter.overdue} onCheckedChange={() => setFilter({ ...filter, overdue: !filter.overdue })} />
            Overdue only
          </label>
          {!isAdmin && (
            <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
              <Checkbox checked={filter.mine} onCheckedChange={() => setFilter({ ...filter, mine: !filter.mine })} />
              Assigned to me
            </label>
          )}
          {(filter.q || filter.status || filter.priority || filter.assignee_id || filter.overdue || filter.mine) && (
            <Button variant="ghost" size="sm"
              onClick={() => setFilter({ status:"", priority:"", assignee_id:"", overdue:false, mine:false, q:"" })}
              className="text-slate-400 hover:text-slate-200">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-4">
          {/* Task list */}
          <div className="space-y-2" data-testid="tasks-list">
            {tasks.length === 0 && (
              <div className="rounded-lg border hairline surface-1 p-10 text-center text-sm text-slate-500">
                No tasks match the current filters.
              </div>
            )}
            {tasks.map((t) => (
              <TaskRow key={t.id} t={t} user={user} isAdmin={isAdmin}
                onOpen={() => setDetailId(t.id)}
                onStatus={setStatus}
                onEdit={() => setEditTarget(t)}
                onDelete={() => removeTask(t)} />
            ))}
          </div>

          {/* Per-assignee sidebar */}
          <div className="rounded-lg border hairline surface-1 p-3">
            <div className="text-xs uppercase tracking-wider text-slate-500 num mb-2">By assignee</div>
            <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
              {(summary.by_assignee || []).length === 0 && (
                <div className="text-xs text-slate-500 text-center py-8">Nobody assigned.</div>
              )}
              {(summary.by_assignee || []).map((a) => (
                <button key={a.assignee_id}
                  onClick={() => setFilter({ ...filter, assignee_id: a.assignee_id })}
                  className="w-full text-left px-2 py-1.5 rounded border hairline surface-2 hover:border-sky-500/40 flex items-center justify-between gap-2"
                  data-testid={`assignee-${a.assignee_id}`}>
                  <div className="text-sm truncate">{a.assignee_name}</div>
                  <div className="text-right">
                    <span className="num text-xs text-slate-300">{a.open} open</span>
                    {a.overdue > 0 && <span className="num text-[10px] text-red-300 ml-2">{a.overdue} overdue</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <TaskAddEditDialog
        open={addOpen || !!editTarget}
        onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditTarget(null); } }}
        target={editTarget} staff={staff}
        onDone={() => { setAddOpen(false); setEditTarget(null); load(); }} />
      <TaskDetailDialog
        open={!!detailId} onOpenChange={() => setDetailId(null)}
        taskId={detailId} user={user} isAdmin={isAdmin}
        onChanged={load} />
    </Layout>
  );
}

function ScoreCard({ label, value, tone }) {
  const toneCls = tone === "red" ? "text-red-300"
    : tone === "emerald" ? "text-emerald-300"
    : tone === "sky" ? "text-sky-300" : "text-slate-100";
  return (
    <div className="rounded-lg border hairline surface-1 p-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 num">{label}</div>
      <div className={`text-2xl font-semibold num mt-1 tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}

function TaskRow({ t, user, isAdmin, onOpen, onStatus, onEdit, onDelete }) {
  const myProgress = t.assignee_progress?.[user?.id]?.status || null;
  const isAssignee = (t.assignee_ids || []).includes(user?.id);
  const due = t.due_date ? `${t.due_date}${t.due_time ? " " + t.due_time : ""}` : "No due date";
  return (
    <div className={`rounded-lg border hairline p-3 cursor-pointer hover:border-sky-500/40 transition
      ${t.is_overdue ? "surface-2 border-red-500/30" : "surface-1"}`}
      onClick={onOpen} data-testid={`task-row-${t.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium truncate">{t.title}</div>
            <span className={`text-[10px] uppercase num px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[t.priority]}`}>
              <Flag className="w-2.5 h-2.5 inline mr-0.5" /> {t.priority}
            </span>
            <span className={`text-[10px] uppercase num px-1.5 py-0.5 rounded border ${STATUS_COLORS[t.status]}`}>
              {t.status}
            </span>
            {t.is_overdue && (
              <span className="text-[10px] uppercase num px-1.5 py-0.5 rounded border text-red-300 border-red-500/40 bg-red-500/10">
                <AlertTriangle className="w-2.5 h-2.5 inline mr-0.5" /> Overdue
              </span>
            )}
            {(t.attachments || []).length > 0 && (
              <span className="text-[10px] text-slate-500 num">
                <Paperclip className="w-3 h-3 inline mr-0.5" /> {t.attachments.length}
              </span>
            )}
            {(t.comments || []).length > 0 && (
              <span className="text-[10px] text-slate-500 num">
                <MessageCircle className="w-3 h-3 inline mr-0.5" /> {t.comments.length}
              </span>
            )}
          </div>
          {t.description && (
            <div className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</div>
          )}
          <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-2 flex-wrap">
            <span>Due: <span className={`num ${t.is_overdue ? "text-red-300" : "text-slate-300"}`}>{due}</span></span>
            <span>·</span>
            <span>Assigned: <span className="text-slate-300">{(t.assignee_names || []).join(", ") || "—"}</span></span>
            {isAdmin && (
              <>
                <span>·</span>
                <span>By: <span className="text-slate-300">{t.created_by_name}</span></span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-wrap" onClick={(e) => e.stopPropagation()}>
          {isAssignee && myProgress !== "Completed" && t.status !== "Cancelled" && (
            <>
              {myProgress !== "In progress" && (
                <Button size="sm" variant="ghost" onClick={() => onStatus(t, "In progress")}
                  className="h-7 px-2 text-sky-300" data-testid={`start-${t.id}`}>
                  <PlayCircle className="w-3.5 h-3.5 mr-1" /> Start
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => onStatus(t, "Completed")}
                className="h-7 px-2 text-emerald-300" data-testid={`done-${t.id}`}>
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Done
              </Button>
            </>
          )}
          {isAdmin && (
            <>
              <Button size="sm" variant="ghost" onClick={onEdit}
                className="h-7 px-2 text-slate-400">Edit</Button>
              <Button size="sm" variant="ghost" onClick={onDelete}
                className="h-7 px-2 text-red-400">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TaskAddEditDialog({ open, onOpenChange, target, staff, onDone }) {
  const empty = {
    title: "", description: "", priority: "Normal",
    due_date: "", due_time: "", assignee_ids: [], status: "Open",
  };
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (target) {
      setForm({
        title: target.title, description: target.description || "",
        priority: target.priority || "Normal",
        due_date: target.due_date || "", due_time: target.due_time || "",
        assignee_ids: target.assignee_ids || [],
        status: target.status || "Open",
      });
    } else { setForm(empty); }
    setFiles([]);
  }, [open, target]); // eslint-disable-line

  const toggleAssignee = (id) => {
    const has = form.assignee_ids.includes(id);
    setForm({ ...form, assignee_ids: has ? form.assignee_ids.filter((x) => x !== id) : [...form.assignee_ids, id] });
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    if (form.assignee_ids.length === 0) { toast.error("Pick at least one assignee"); return; }
    try {
      if (target) {
        await api.put(`/tasks/${target.id}`, {
          title: form.title.trim(), description: form.description.trim(),
          priority: form.priority, due_date: form.due_date || null,
          due_time: form.due_time || null, assignee_ids: form.assignee_ids,
          status: form.status,
        });
        toast.success("Task updated");
      } else {
        const atts = [];
        for (const f of files) {
          atts.push({ filename: f.name, content_type: f.type || "application/octet-stream",
                      data_base64: await fileToBase64(f) });
        }
        await api.post("/tasks", {
          title: form.title.trim(), description: form.description.trim(),
          priority: form.priority, due_date: form.due_date || null,
          due_time: form.due_time || null,
          assignee_ids: form.assignee_ids, attachments: atts,
        });
        toast.success("Task created");
      }
      onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg"
        data-testid="task-add-edit-dialog">
        <DialogHeader><DialogTitle>{target ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
          <div>
            <Label className="text-xs text-slate-400">Title</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Follow up with Orchid Kitchen"
              className="bg-[#0a0e1a] border-slate-800 mt-1" data-testid="task-title" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Description</Label>
            <Textarea value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3} placeholder="Steps, context, links…"
              className="bg-[#0a0e1a] border-slate-800 mt-1 text-sm" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                  <SelectItem value="Urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Due date</Label>
              <Input type="date" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Due time</Label>
              <Input type="time" value={form.due_time}
                onChange={(e) => setForm({ ...form, due_time: e.target.value })}
                className="bg-[#0a0e1a] border-slate-800 mt-1 num" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Assignees ({form.assignee_ids.length})</Label>
            <div className="grid grid-cols-2 gap-1.5 mt-1 p-2 rounded-md border hairline surface-2 max-h-40 overflow-y-auto">
              {staff.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer"
                  data-testid={`assignee-pick-${s.username}`}>
                  <Checkbox checked={form.assignee_ids.includes(s.id)}
                    onCheckedChange={() => toggleAssignee(s.id)} />
                  <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                  <span className="truncate">{s.display_name}</span>
                  {s.role === "admin" && <span className="text-[9px] text-emerald-400">admin</span>}
                </label>
              ))}
            </div>
          </div>
          {!target && (
            <div>
              <Label className="text-xs text-slate-400">Attachments (optional)</Label>
              <Input type="file" multiple
                onChange={(e) => setFiles(Array.from(e.target.files || []))}
                className="bg-[#0a0e1a] border-slate-800 mt-1 file:text-slate-300" />
            </div>
          )}
          {target && (
            <div>
              <Label className="text-xs text-slate-400">Status (admin override)</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger className="bg-[#0a0e1a] border-slate-800 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Open</SelectItem>
                  <SelectItem value="In progress">In progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-sky-600 hover:bg-sky-500"
            data-testid="task-submit">{target ? "Save changes" : "Create task"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TaskDetailDialog({ open, onOpenChange, taskId, user, isAdmin, onChanged }) {
  const [task, setTask] = useState(null);
  const [draft, setDraft] = useState("");
  const [file, setFile] = useState(null);

  const load = useCallback(async () => {
    if (!taskId) return;
    try {
      const { data } = await api.get(`/tasks/${taskId}`);
      setTask(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }, [taskId]);

  useEffect(() => { if (open) load(); else setTask(null); }, [open, load]);

  const addComment = async () => {
    if (!draft.trim()) return;
    try {
      const payload = { content: draft.trim() };
      if (file) {
        payload.attachment = {
          filename: file.name, content_type: file.type || "application/octet-stream",
          data_base64: await fileToBase64(file),
        };
      }
      await api.post(`/tasks/${taskId}/comments`, payload);
      setDraft(""); setFile(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openAttachment = async (kind, idx) => {
    try {
      const res = await api.get(`/tasks/${taskId}/attachment/${kind}/${idx}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open"); }
  };

  if (!open || !task) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-lg">
          <div className="text-sm text-slate-500">Loading…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const isAssignee = (task.assignee_ids || []).includes(user?.id);
  const myProgress = task.assignee_progress?.[user?.id]?.status || null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f1626] border-slate-800 text-slate-100 max-w-xl" data-testid="task-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <ClipboardList className="w-4 h-4 text-sky-400" /> {task.title}
            <span className={`text-[10px] uppercase num px-1.5 py-0.5 rounded border ${PRIORITY_COLORS[task.priority]}`}>
              {task.priority}
            </span>
            <span className={`text-[10px] uppercase num px-1.5 py-0.5 rounded border ${STATUS_COLORS[task.status]}`}>
              {task.status}
            </span>
            {task.is_overdue && (
              <span className="text-[10px] uppercase num px-1.5 py-0.5 rounded border text-red-300 border-red-500/40 bg-red-500/10">
                Overdue
              </span>
            )}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {task.description && (
            <div className="text-sm text-slate-300 whitespace-pre-wrap">{task.description}</div>
          )}
          <div className="grid grid-cols-2 gap-2 text-xs">
            <Row k="Due" v={task.due_date ? `${task.due_date}${task.due_time ? " " + task.due_time : ""}` : "—"} />
            <Row k="Created by" v={task.created_by_name} />
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500 num mb-1">Assignees</div>
            <div className="flex flex-wrap gap-1.5">
              {(task.assignee_ids || []).map((id, i) => {
                const prog = task.assignee_progress?.[id]?.status || "Open";
                return (
                  <span key={id} className={`text-[11px] num px-2 py-0.5 rounded border ${STATUS_COLORS[prog] || STATUS_COLORS.Open}`}>
                    {task.assignee_names?.[i] || id} · {prog}
                  </span>
                );
              })}
            </div>
          </div>

          {(task.attachments || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase text-slate-500 num mb-1">Attachments</div>
              <div className="flex flex-wrap gap-1.5">
                {task.attachments.map((a, i) => (
                  <Button key={i} size="sm" variant="outline"
                    onClick={() => openAttachment("task", i)}
                    className="border-slate-700 h-7 text-xs">
                    <Paperclip className="w-3.5 h-3.5 mr-1" /> {a.filename}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Comments / request info thread */}
          <div>
            <div className="text-[10px] uppercase text-slate-500 num mb-1">Activity / request info</div>
            <div className="space-y-1.5">
              {(task.comments || []).length === 0 && (
                <div className="text-xs text-slate-500">No messages yet.</div>
              )}
              {(task.comments || []).map((c, i) => {
                const mine = c.author_id === user?.id;
                return (
                  <div key={c.id || i} className={`rounded-md border hairline p-2 text-sm
                    ${mine ? "bg-sky-600/10 border-sky-500/30" : "surface-2"}`}>
                    <div className="flex items-center justify-between mb-0.5 gap-2">
                      <span className="text-[10px] uppercase num" style={{ color: c.author_color }}>
                        {c.author_name} · {c.author_role}
                      </span>
                      <span className="text-[10px] text-slate-500 num">{fmtTime(c.created_at)}</span>
                    </div>
                    <div className="whitespace-pre-wrap break-words text-slate-200">{c.content}</div>
                    {c.attachment?.filename && (
                      <Button size="sm" variant="outline"
                        onClick={() => openAttachment("comment", i)}
                        className="mt-1 border-slate-700 h-6 text-[11px]">
                        <Paperclip className="w-3 h-3 mr-1" /> {c.attachment.filename}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {(isAdmin || isAssignee || task.created_by === user?.id) && task.status !== "Cancelled" && (
          <div className="border-t hairline pt-3 space-y-2">
            <Textarea value={draft} onChange={(e) => setDraft(e.target.value)}
              placeholder={isAssignee
                ? "Ask for more info or update admin…"
                : "Reply / clarify…"}
              rows={2}
              className="bg-[#0a0e1a] border-slate-800 text-sm"
              data-testid="task-comment-input" />
            <div className="flex items-center gap-2">
              <Input type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="bg-[#0a0e1a] border-slate-800 h-8 text-xs file:text-slate-300 flex-1" />
              <Button size="sm" onClick={addComment} disabled={!draft.trim()}
                className="bg-sky-600 hover:bg-sky-500" data-testid="task-comment-submit">
                Send
              </Button>
            </div>
            {isAssignee && myProgress !== "Completed" && task.status !== "Completed" && (
              <div className="flex items-center gap-2 pt-2 border-t hairline">
                <span className="text-[10px] uppercase text-slate-500 num">Your status:</span>
                {["Open", "In progress", "Completed"].map((s) => (
                  <Button key={s} size="sm"
                    variant={myProgress === s ? "default" : "ghost"}
                    onClick={async () => {
                      await api.post(`/tasks/${task.id}/status`, { status: s });
                      await load(); onChanged?.();
                    }}
                    className={`h-7 px-2 text-xs ${myProgress === s ? "bg-sky-600" : ""}`}
                    data-testid={`self-status-${s.replace(" ", "-")}`}>
                    {s}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-2">
      <div className="text-slate-500">{k}</div>
      <div className="text-right text-slate-200 break-words min-w-0">{v}</div>
    </div>
  );
}
